import { useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { feedScroll } from '@/lib/feedScroll'
import { getFeedItemId } from '@/lib/feedItem'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Users, MessageCircle, UserPlus } from 'lucide-react'
import type { FeedItem, FeedType } from '@olimhub/shared'
import { feedsApi, accountsApi } from '@/api'
import { useFeedStore } from '@/store/feed.store'
import { useAuthStore } from '@/store/auth.store'
import { usePostForm } from '@/context/PostFormContext'
import { FeedList, FeedTypeChips } from '@/components/feed'
import { HeroBand, PostCardSkeleton } from '@/components/common'
import { SuggestedAccounts } from '@/components/profile'
import { useSuggestionsStore } from '@/store/suggestions.store'

const PULL_THRESHOLD = 80
const PULL_MAX = 120
const REFRESH_INTERVAL = 30_000
const AWAY_THRESHOLD = 5 * 60 * 1000

// ── Home ──────────────────────────────────────────────────────────────────────

export function Home() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const activeTag = searchParams.get('tag') ?? undefined
  const { selectedFeed, selectedTypes, setSelectedTypes, toggleType, refreshSignal, guestFeedLanguages } = useFeedStore()
  const { isAuthenticated, account } = useAuthStore()
  const { openNew } = usePostForm()
  const feedLangs = isAuthenticated
    ? (account?.feedLanguages?.length ? account.feedLanguages : undefined)
    : (guestFeedLanguages.length ? guestFeedLanguages : undefined)

  // Unauthenticated users fall back from following → local
  const effectiveFeed: FeedType =
    !isAuthenticated && selectedFeed === 'following' ? 'local' : selectedFeed

  // ── Suggested accounts strip ──────────────────────────────────────────────
  const { fetchSuggestions } = useSuggestionsStore()
  const LOW_FOLLOWING_THRESHOLD = 5

  const { data: profileData } = useQuery({
    queryKey: ['profile', account?.username],
    queryFn: () => accountsApi.getByUsername(account!.username),
    enabled: isAuthenticated && !!account?.username && effectiveFeed === 'following',
    staleTime: 60_000,
  })

  const followingCount = profileData?.stats.followingCount ?? null
  // Require followingCount to be known before showing anything — prevents
  // skeleton flash for users who already have > 5 followings.
  const showStrip = effectiveFeed === 'following' && isAuthenticated &&
    followingCount !== null && followingCount <= LOW_FOLLOWING_THRESHOLD

  // Desktop: keep strip open while mouse is inside after threshold is crossed.
  const [mouseInsideStrip, setMouseInsideStrip] = useState(false)
  const isMouseDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches

  // Mobile: 10-second grace period after threshold is crossed.
  const [mobileGrace, setMobileGrace] = useState(false)
  const prevShowStripRef = useRef(false)
  useEffect(() => {
    const wasShowing = prevShowStripRef.current
    prevShowStripRef.current = showStrip
    // Only trigger when strip transitions true → false on a touch device
    if (isMouseDevice || showStrip || !wasShowing) return
    setMobileGrace(true)
    const timer = setTimeout(() => setMobileGrace(false), 10_000)
    return () => clearTimeout(timer)
  }, [showStrip, isMouseDevice])

  const displayStrip = showStrip ||
    (isMouseDevice && mouseInsideStrip && effectiveFeed === 'following' && isAuthenticated) ||
    (!isMouseDevice && mobileGrace && effectiveFeed === 'following' && isAuthenticated)

  const stripHeading = followingCount === 0
    ? t('suggestions.follow_cta')
    : t('suggestions.people_you_might_know')

  useEffect(() => {
    if (isAuthenticated && effectiveFeed === 'following') void fetchSuggestions()
  }, [isAuthenticated, effectiveFeed, fetchSuggestions])

  // ── Query ─────────────────────────────────────────────────────────────────
  const queryKey = ['feed', effectiveFeed, activeTag, feedLangs, isAuthenticated, selectedTypes]

  const queryClient = useQueryClient()

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching, refetch } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) => {
        const cursor = pageParam as string | undefined
        const params = { cursor, languages: feedLangs, tag: activeTag, types: selectedTypes }
        return effectiveFeed === 'following' ? feedsApi.getFollowing(params) : feedsApi.getLocal(params)
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    })

  const allItems: FeedItem[] = data?.pages.flatMap((p) => p.items) ?? []

  // ── Background polling + away-return refresh ──────────────────────────────
  const queryClientRef = useRef(queryClient)
  queryClientRef.current = queryClient
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hiddenAtRef = useRef<number | null>(null)

  useEffect(() => {
    function invalidateFeed() {
      void queryClientRef.current.invalidateQueries({ queryKey: ['feed'] })
    }

    function startPolling() {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = setInterval(invalidateFeed, REFRESH_INTERVAL)
    }

    if (!document.hidden) startPolling()

    function onVisibilityChange() {
      if (document.hidden) {
        if (intervalRef.current) clearInterval(intervalRef.current)
        hiddenAtRef.current = Date.now()
      } else {
        const hiddenAt = hiddenAtRef.current
        hiddenAtRef.current = null
        if (hiddenAt !== null && Date.now() - hiddenAt > AWAY_THRESHOLD) {
          invalidateFeed()
        }
        startPolling()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  // ── Scroll restoration (back from post detail) ────────────────────────────
  const scrollRestored = useRef(false)
  useEffect(() => {
    if (scrollRestored.current || allItems.length === 0) return
    const top = feedScroll.consume()
    if (top === null) return
    scrollRestored.current = true
    // Double rAF: first frame React commits DOM, second frame browser lays out
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById('feed-scroll')?.scrollTo({ top, behavior: 'instant' })
      })
    })
  }, [allItems.length])

  // ── New content banner ────────────────────────────────────────────────────
  const seenIdsRef = useRef<Set<string>>(new Set())
  const [newPostsCount, setNewPostsCount] = useState(0)
  const [showBanner, setShowBanner] = useState(false)
  const isManualRefreshingRef = useRef(false)

  useEffect(() => {
    if (!data || isLoading) return
    const items = data.pages[0]?.items ?? []
    if (seenIdsRef.current.size === 0) {
      for (const item of allItems) seenIdsRef.current.add(getFeedItemId(item))
      return
    }
    if (isManualRefreshingRef.current) return
    let newCount = 0
    for (const item of items) {
      if (seenIdsRef.current.has(getFeedItemId(item))) break
      newCount++
    }
    if (newCount > 0) {
      if (window.scrollY < 80) {
        for (const item of items) seenIdsRef.current.add(getFeedItemId(item))
      } else {
        setNewPostsCount((prev) => prev + newCount)
        setShowBanner(true)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading])

  const handleBannerRefresh = useCallback(async () => {
    isManualRefreshingRef.current = true
    setShowBanner(false)
    setNewPostsCount(0)
    seenIdsRef.current = new Set()
    await refetch()
    window.scrollTo({ top: 0, behavior: 'smooth' })
    isManualRefreshingRef.current = false
  }, [refetch])

  // Post-creation refresh signal
  const prevRefreshSignalRef = useRef(refreshSignal)
  useEffect(() => {
    if (refreshSignal !== prevRefreshSignalRef.current) {
      prevRefreshSignalRef.current = refreshSignal
      void handleBannerRefresh()
    }
  }, [refreshSignal, handleBannerRefresh])

  // ── Pull-to-refresh ───────────────────────────────────────────────────────
  const feedRef = useRef<HTMLDivElement>(null)
  const touchStartYRef = useRef<number | null>(null)
  const [pullDist, setPullDist] = useState(0)
  const pullDistRef = useRef(0)
  const allItemsRef = useRef<FeedItem[]>([])
  allItemsRef.current = allItems
  const isPullingRef = useRef(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    const el = feedRef.current
    if (!el) return

    // The window never scrolls — find the actual overflow scroll container (<main>)
    let scrollEl: HTMLElement = document.documentElement
    let p = el.parentElement
    while (p) {
      if (/(auto|scroll)/.test(getComputedStyle(p).overflowY)) { scrollEl = p; break }
      p = p.parentElement
    }

    const scrollTop = () => scrollEl.scrollTop

    const onTouchMove = (e: TouchEvent) => {
      if (touchStartYRef.current === null) {
        scrollEl.removeEventListener('touchmove', onTouchMove)
        return
      }
      const delta = e.touches[0]!.clientY - touchStartYRef.current

      if (delta <= 0) {
        touchStartYRef.current = null
        isPullingRef.current = false
        scrollEl.removeEventListener('touchmove', onTouchMove)
        return
      }

      if (scrollTop() === 0) {
        isPullingRef.current = true
        e.preventDefault()
        pullDistRef.current = Math.min(delta * 0.4, PULL_MAX)
        setPullDist(pullDistRef.current)
      }
    }

    const onTouchStart = (e: TouchEvent) => {
      if (scrollTop() > 0) return
      touchStartYRef.current = e.touches[0]!.clientY
      isPullingRef.current = false
      // Non-passive listener attached only when at the very top of the feed
      scrollEl.addEventListener('touchmove', onTouchMove, { passive: false })
    }

    const onTouchEnd = async () => {
      scrollEl.removeEventListener('touchmove', onTouchMove)
      const dist = pullDistRef.current
      touchStartYRef.current = null
      isPullingRef.current = false
      pullDistRef.current = 0
      setPullDist(0)
      if (dist >= PULL_THRESHOLD) {
        setIsRefreshing(true)
        isManualRefreshingRef.current = true
        await refetch()
        seenIdsRef.current = new Set(allItemsRef.current.map((i) => getFeedItemId(i)))
        setShowBanner(false)
        setNewPostsCount(0)
        isManualRefreshingRef.current = false
        setIsRefreshing(false)
      }
    }

    scrollEl.addEventListener('touchstart', onTouchStart, { passive: true })
    scrollEl.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      scrollEl.removeEventListener('touchstart', onTouchStart)
      scrollEl.removeEventListener('touchmove', onTouchMove)
      scrollEl.removeEventListener('touchend', onTouchEnd)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch])

  const pullProgress = Math.min(pullDist / PULL_THRESHOLD, 1)
  const showPullIndicator = pullDist > 10 || isRefreshing

  // ── Empty state helpers ───────────────────────────────────────────────────
  function renderEmptyState() {
    const iconStyle: React.CSSProperties = { color: 'var(--text-faint)', marginBottom: 12 }
    const titleStyle: React.CSSProperties = {
      margin: '0 0 6px',
      fontFamily: 'var(--font-sans)',
      fontWeight: 600,
      fontSize: 'var(--fs-sm)',
      color: 'var(--text-strong)',
    }
    const subStyle: React.CSSProperties = {
      margin: '0 0 20px',
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--fs-sm)',
      color: 'var(--text-muted)',
      maxWidth: 280,
    }
    const ctaStyle: React.CSSProperties = {
      padding: '10px 20px',
      border: 'none',
      borderRadius: 'var(--radius-pill)',
      background: 'var(--brand)',
      color: 'var(--on-brand)',
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--fs-sm)',
      fontWeight: 700,
      cursor: 'pointer',
    }
    const wrap: React.CSSProperties = {
      padding: '56px 24px',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }

    // Nível 2 chips active with no results — includes the genuine "no
    // Action content exists yet" case, deliberately identical to any other
    // empty-filter state (see SPEC_nav_menu_refactor.md).
    if (selectedTypes.length > 0) {
      return (
        <div style={wrap}>
          <MessageCircle size={48} strokeWidth={1.5} style={iconStyle} />
          <p style={titleStyle}>{t('feed.empty_filtered')}</p>
          <p style={subStyle} />
          <button onClick={() => setSelectedTypes([])} style={{ border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--brand)', fontWeight: 600, padding: 0 }}>
            {t('feed.empty_filter_clear')}
          </button>
        </div>
      )
    }
    if (effectiveFeed === 'following') {
      return (
        <div style={wrap}>
          <UserPlus size={48} strokeWidth={1.5} style={iconStyle} />
          <p style={titleStyle}>{t('feed.empty_following')}</p>
          <p style={subStyle}>Find people to follow in the local feed</p>
        </div>
      )
    }
    if (!isAuthenticated) {
      return (
        <div style={wrap}>
          <Users size={48} strokeWidth={1.5} style={iconStyle} />
          <p style={titleStyle}>Welcome to OlimHub</p>
          <p style={subStyle}>Sign up to join the conversation</p>
          <button onClick={() => navigate('/signup')} style={ctaStyle}>{t('welcome.signup')}</button>
        </div>
      )
    }
    return (
      <div style={wrap}>
        <MessageCircle size={48} strokeWidth={1.5} style={iconStyle} />
        <p style={titleStyle}>{t('feed.empty_local')}</p>
        <button onClick={() => openNew('post')} style={{ ...ctaStyle, marginTop: 4 }}>
          Create first post
        </button>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        onMouseEnter={isMouseDevice ? () => setMouseInsideStrip(true) : undefined}
        onMouseLeave={isMouseDevice ? () => setMouseInsideStrip(false) : undefined}
        style={{
          overflow: 'hidden',
          maxHeight: displayStrip ? 300 : 0,
          opacity: displayStrip ? 1 : 0,
          transition: 'max-height 0.35s ease, opacity 0.25s ease',
          pointerEvents: displayStrip ? 'auto' : 'none',
        }}
      >
        <SuggestedAccounts mode="strip" heading={stripHeading} />
      </div>
      <div ref={feedRef} style={{ maxWidth: 'var(--feed-max)', margin: '0 auto', paddingBottom: 80 }}>

      <FeedTypeChips selectedTypes={selectedTypes} onToggle={toggleType} />

      {!isAuthenticated && <HeroBand />}

      {/* "peek inside · read-only" divider for unauthenticated */}
      {!isAuthenticated && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 16px 6px',
            color: 'var(--text-faint)',
            fontFamily: 'var(--font-sans)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          <span style={{ height: 1, flex: 1, background: 'var(--border)' }} />
          {t('welcome.peek')}
          <span style={{ height: 1, flex: 1, background: 'var(--border)' }} />
        </div>
      )}

      {/* Pull-to-refresh indicator */}
      {showPullIndicator && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: Math.max(pullDist, isRefreshing ? 48 : 0),
            overflow: 'hidden',
            transition: isRefreshing ? 'height 0.2s ease' : undefined,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '2.5px solid var(--brand)',
              borderTopColor: isRefreshing || pullProgress >= 1 ? 'transparent' : 'var(--brand)',
              animation: isRefreshing ? 'spin 0.7s linear infinite' : undefined,
              transform: `rotate(${pullProgress * 180}deg)`,
              transition: isRefreshing ? undefined : 'transform 0.05s linear',
              opacity: pullProgress,
            }}
          />
        </div>
      )}

      {/* New content banner */}
      {showBanner && newPostsCount > 0 && (
        <button
          type="button"
          onClick={() => void handleBannerRefresh()}
          style={{
            width: '100%',
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--brand)',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 'var(--fs-sm)',
            color: '#fff',
          }}
        >
          {t('feed.new_posts', { count: newPostsCount })}
        </button>
      )}

      {/* Feed */}
      <div style={!isAuthenticated ? { opacity: 0.9 } : undefined}>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px' }}>
            <PostCardSkeleton />
            <PostCardSkeleton />
            <PostCardSkeleton />
          </div>
        ) : allItems.length === 0 && !isFetching ? renderEmptyState() : (
          <FeedList
            items={allItems}
            hasMore={hasNextPage ?? false}
            isLoading={isLoading}
            isFetchingMore={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            showCTA={!isAuthenticated && effectiveFeed === 'local'}
          />
        )}
      </div>
    </div>
    </>
  )
}
